import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'
import {
  TaskPriorityLabel,
  TaskStatusLabel,
  type CaseListResponse,
  type CreateTaskInput,
  type TaskDto,
  type TaskPriorityT,
  type TaskStatusT,
  type UserListItem,
} from '@toga/shared'

interface Props {
  initial: TaskDto | null
  defaultCaseId?: string | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}

export default function TaskFormModal({ initial, defaultCaseId, onClose, onSaved }: Props) {
  const isEdit = initial !== null
  const [form, setForm] = useState<CreateTaskInput>({
    caseId: initial?.caseId ?? defaultCaseId ?? null,
    title: initial?.title ?? '',
    description: initial?.description ?? null,
    status: initial?.status ?? 'PENDIENTE',
    priority: initial?.priority ?? 'MEDIA',
    assignedToId: initial?.assignedToId ?? null,
    dueAt: initial?.dueAt?.slice(0, 10) ?? null,
  })
  const [users, setUsers] = useState<UserListItem[]>([])
  const [cases, setCases] = useState<CaseListResponse['items']>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.get<{ items: UserListItem[] }>('/users').then((r) => setUsers(r.items)).catch(() => setUsers([]))
    api.get<CaseListResponse>('/cases?pageSize=100').then((r) => setCases(r.items)).catch(() => setCases([]))
  }, [])

  const upd = useCallback(<K extends keyof CreateTaskInput>(k: K, v: CreateTaskInput[K]) => {
    setForm((f) => ({ ...f, [k]: v }))
  }, [])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) {
      setError('Indica un título')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const payload: CreateTaskInput = {
        ...form,
        title: form.title.trim(),
        description: form.description?.trim() || null,
        caseId: form.caseId || null,
        assignedToId: form.assignedToId || null,
        dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : null,
      }
      if (isEdit && initial) {
        await api.patch(`/tasks/${initial.id}`, payload)
      } else {
        await api.post('/tasks', payload)
      }
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar tarea' : 'Nueva tarea'} maxWidth="max-w-xl">
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block">
          <span className="text-xs t-muted">Título *</span>
          <input
            value={form.title}
            onChange={(e) => upd('title', e.target.value)}
            className={input}
            required
            autoFocus
          />
        </label>

        <label className="block">
          <span className="text-xs t-muted">Descripción</span>
          <textarea
            value={form.description ?? ''}
            onChange={(e) => upd('description', e.target.value || null)}
            rows={3}
            className={input}
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Estado</span>
            <select
              value={form.status}
              onChange={(e) => upd('status', e.target.value as TaskStatusT)}
              className={input}
            >
              {Object.entries(TaskStatusLabel).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs t-muted">Prioridad</span>
            <select
              value={form.priority}
              onChange={(e) => upd('priority', e.target.value as TaskPriorityT)}
              className={input}
            >
              {Object.entries(TaskPriorityLabel).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Asignar a</span>
            <select
              value={form.assignedToId ?? ''}
              onChange={(e) => upd('assignedToId', e.target.value || null)}
              className={input}
            >
              <option value="">— sin asignar —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.fullName}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs t-muted">Vencimiento</span>
            <input
              type="date"
              value={form.dueAt ?? ''}
              onChange={(e) => upd('dueAt', e.target.value || null)}
              className={input}
            />
          </label>
        </div>

        <label className="block">
          <span className="text-xs t-muted">Expediente</span>
          <select
            value={form.caseId ?? ''}
            onChange={(e) => upd('caseId', e.target.value || null)}
            className={input}
            disabled={Boolean(defaultCaseId)}
          >
            <option value="">— sin expediente —</option>
            {cases.map((c) => (
              <option key={c.id} value={c.id}>{c.code} · {c.title}</option>
            ))}
          </select>
        </label>

        {error && <p className="text-xs text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Guardar' : 'Crear'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
